import { z } from "zod";

export const userSettingsSchema = z.object({
  first_name: z.string(),
  last_name: z.string(),
});

export const personSchema = z.object({
  id: z.number(),
  email: z.string().email(),
  first_name: z.string(),
  last_name: z.string(),
  phone: z.string().nullable(),
  phone_country: z.string().nullable(),
  account_type: z.string(),
  is_active: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
  avatar: z.string().nullable(),
});

export const emailSchema = z
  .string({
    message: "Adres e-mail jest wymagany",
  })
  .email({ message: "Błędny adres e-mail" });

export const updateEmailSchema = z
  .object({
    email: emailSchema,
    email_confirmation: z.string({
      message: "Potwierdzenie adresu e-mail jest wymagane",
    }),
    password: z
      .string({ message: "Hasło jest wymagane" })
      .min(1, { message: "Hasło jest wymagane" }),
  })
  .refine((data) => data.email === data.email_confirmation, {
    message: "Maile różnią się od siebie",
    path: ["email_confirmation"],
  });

export const resetPasswordTokenSchema = z.string().regex(/^[0-9A-Fa-f]{64}$/);

export const newEmailTokenSchema = z.string().regex(/^[0-9A-Za-z]{64}$/);

export const loginFormSchema = z.object({
  email: emailSchema,
  password: z
    .string({ message: "Hasło jest wymagane" })
    .min(1, { message: "Hasło jest wymagane" }),
  remember: z.boolean().optional(),
});

export const resetPasswordSchema = z
  .object({
    password: z.string({ message: "Hasło jest wymagane" }).min(8, {
      message: "Hasło musi mieć co najmniej 8 znaków",
    }),
    password_confirmation: z.string({
      message: "Potwierdzenie hasła jest wymagane",
    }),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: "Hasła różnią się od siebie",
    path: ["password_confirmation"],
  });

export const changePasswordSchema = z
  .object({
    current_password: z.string({ message: "Hasło jest wymagane" }).min(8, {
      message: "Hasło musi mieć co najmniej 8 znaków",
    }),
    password: z
      .string({ message: "Nowe hasło jest wymagane" })
      .min(8, { message: "Hasło musi mieć co najmniej 8 znaków" })
      .regex(/[a-z]/, {
        message: "Hasło musi zawierać przynajmniej 1 małą literę",
      })
      .regex(/[A-Z]/, {
        message: "Hasło musi zawierać przynajmniej 1 dużą literę",
      })
      .regex(/[0-9]/, { message: "Hasło musi zawierać przynajmniej 1 cyfrę" })
      .regex(/[^a-zA-Z0-9]/, {
        message: "Hasło musi zawierać przynajmniej 1 symbol",
      }),
    password_confirmation: z.string({
      message: "Potwierdzenie hasła jest wymagane",
    }),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: "Hasła różnią się od siebie",
    path: ["password_confirmation"],
  });

export const personalDataSchema = z.object({
  first_name: z.string().min(1, { message: "Musisz podać imię" }),
  last_name: z.string().min(1, { message: "Musisz podać nazwisko" }),
  email: emailSchema,
  password: z
    .string()
    .min(8, { message: "Hasło musi mieć co najmniej 8 znaków" }),
});

export const menuItemSchema = z.object({
  label: z.string(),
  to: z.string(),
  icon: z.string(),
});

export const remindPasswordEmailFormSchema = z.object({
  email: z
    .string({ message: "Adres e-mail jest wymagany" })
    .email({ message: "Błędny adres e-mail" }),
});

export const journalItemSchema = z.object({
  id: z.number(),
  client_project_id: z.number(),
  author_user_id: z.number(),
  title: z.string(),
  content: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  author: personSchema,
  main_image: z.string().nullable(),
  images: z.array(z.string()).nullable(),
  attachments: z.array(z.string()).nullable(),
});

export const journalItemsSchema = z.array(journalItemSchema);

export const statusColorsSchema = z.enum([
  "red",
  "green",
  "orange",
  "slate",
  "sky",
  "grey",
  "purple",
]);

export const statusTypesSchema = z.object({
  color: statusColorsSchema,
  label: z.string(),
});

export const confirmNewEmailSchema = z.object({
  old_email: z.string(),
  email: z.string(),
  token: z.string(),
});

export const decistionStatusSchema = z.enum([
  "approved",
  "rejected",
  "no_decision",
  "pending",
  "feedback_submission",
]);

export const singleDecisionSchema = z.object({
  attachments: z.array(z.string()).nullable(),
  client_project_id: z.number(),
  created_at: z.string(),
  decision_date: z.string(),
  description: z.string(),
  id: z.number(),
  delayed_days: z.number().nullable().optional(),
  name: z.string(),
  status: decistionStatusSchema,
  short_description: z.string().nullable(),
  // status_changed_at: z.string().nullable(),
  updated_at: z.string(),
});

export const decisionsSchema = z.array(singleDecisionSchema);

export const checkboxStatusSchema = z.enum([
  "pending",
  "in_progress_self",
  "requesting_quote",
  "assigned_for_execution",
  "completed",
  "no_decision",
]);

export const singleCheckboxSchema = z.object({
  attachments: z.array(z.string()).nullable(),
  client_project_id: z.number(),
  created_at: z.string(),
  deadline_date: z.string(),
  description: z.string(),
  id: z.number(),
  delayed_days: z.number().nullable(),
  name: z.string(),
  status: checkboxStatusSchema,
  short_description: z.string().nullable(),
  // status_changed_at: z.string().nullable(),
  updated_at: z.string(),
});

export const checkboxesSchema = z.array(singleCheckboxSchema);

export const setCheckboxStatusSchema = z.object({
  checkbox_id: z.number(),
  status: checkboxStatusSchema,
  message_id: z.number().optional(),
  external_message_sent_at: z.string().optional(),
});

export const projectDetailsSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  images: z.array(z.string()).nullable(),
  main_image: z.string().nullable(),
});

export const setDecisionStatusSchema = z.object({
  decision_id: z.number(),
  message_id: z.number().optional().nullable(),
  status: decistionStatusSchema,
  external_message_sent_at: z.coerce.string().optional().nullable(),
});

export const decisionDateSchema = z.object({
  external_message_sent_at: z
    .union([z.string(), z.date()], {
      message: "Musisz podać datę",
    })
    .optional()
    .refine(
      (value) => {
        if (!value) return false; // pole jest wymagane
        if (value instanceof Date) {
          return !isNaN(value.getTime());
        }
        if (typeof value === "string" && value.trim().length === 0) {
          return false;
        }
        const date = new Date(value);
        return !isNaN(date.getTime());
      },
      {
        message: "Musisz podać datę",
      },
    )
    .transform((value) => {
      if (!value) return undefined;
      if (value instanceof Date) {
        return value;
      }
      return new Date(value);
    }),
});

export const scheduleSchema = z.object({
  client_project_id: z.number(),
  completion_date: z.string(),
  created_at: z.string(),
  delay_days: z.number().nullable(),
  description: z.string().nullable(),
  id: z.number(),
  name: z.string(),
  type: z.string(),
  updated_at: z.string(),
});

export const schedulesSchema = z.array(scheduleSchema);

export const contactMessageTypeSchema = z.object({
  id: z.number(),
  name: z.string(),
  account_type: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
});

export const contactMessageTypesSchema = z.array(contactMessageTypeSchema);

export const contactFormBaseSchema = z.object({
  message_type_id: z
    .number({ message: "Musisz wybrać typ wiadomości" })
    .optional(),
  decision_id: z.number({ message: "Musisz podać id" }).optional(),
  subject: z.string({ message: "Musisz coś napisać" }),
  content: z.string({ message: "Musisz coś napisać" }),
});

export const contactFormSchema = contactFormBaseSchema;

// Strona „Kontakt” (bez decyzji): rodzaj wiadomości wymagany — tak jak w API (required_without:decision_id).
export const contactPageFormSchema = contactFormBaseSchema.extend({
  message_type_id: z.number({ message: "Musisz wybrać typ wiadomości" }),
});

export const contactMessageSchema = z.object({
  client_project_id: z.number(),
  ...contactFormBaseSchema.shape,
});

export const documentTypesSchema = z.enum([
  "decision",
  "journal",
  "settlement",
  "annex",
  "agreement",
  "protocol",
  "document",
  "other",
  "checkbox",
]);

export const notificationDataSchema = z.object({
  title: z.string().optional(),
  content: z.string(),
  relatable_type: documentTypesSchema,
  relatable_id: z.number().nullable().optional(),
  name: z.string().optional(),
});

export const singleNotificationSchema = z.object({
  id: z.string(),
  client_project_id: z.number().nullable(),
  type: z.string(),
  notifiable_type: z.string(),
  notifiable_id: z.number(),
  read_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  data: notificationDataSchema,
});

export const notificationsSchema = z.array(singleNotificationSchema);

export const notificationReadSchema = z.object({
  notification_id: z.string(),
});

export const paymentSchema = z.object({
  id: z.number(),
  settlement_id: z.number(),
  amount: z.string(),
  payment_date: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
});

export const paymentsSchema = z.array(paymentSchema);

export const settlementSchema = z.object({
  id: z.number(),
  settlement_stage_id: z.number(),
  name: z.string(),
  amount: z.string(),
  payment_due_date: z.string(),
  status: z.enum(["to_pay", "paid"]), // Assuming possible statuses
  proforma_invoice: z.string().url().nullable(),
  // Lista faktur; stare API zwracało jeden URL albo null — normalizujemy do listy.
  invoice: z.preprocess(
    (value) => (value == null ? [] : Array.isArray(value) ? value : [value]),
    z.array(z.string().url()),
  ),
  order: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
  payments: z.array(paymentSchema).optional(),
});

export const settlementsSchema = z.array(settlementSchema);

export const settlementStageSchema = z.object({
  id: z.number(),
  client_project_id: z.number(),
  settlements_sum_amount: z.string(),
  paid_sum: z.string(),
  left_to_pay: z.string(),
  name: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  settlements: z.array(settlementSchema).optional(),
});

export const settlementStagesSchema = z.array(settlementStageSchema);

export const projectSchema = z.object({
  id: z.number(),
  project_id: z.number(),
  client_user_id: z.number(),
  engineer_user_id: z.number(),
  advisor_user_id: z.number(),
  localization: z.string(),
  estimated_end_date: z.string(),
  agreed_end_date: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  decisions: z.array(singleDecisionSchema).nullable().optional(),
  engineer: personSchema.optional().nullable(),
  advisor: personSchema.optional().nullable(),
  project: projectDetailsSchema.optional(),
  latest_notification: singleNotificationSchema.optional().nullable(),
  progress: z.number().optional().nullable(),
});

export const projecstSchema = z.array(projectSchema);

export const userSchema = z.object({
  email: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  account_type: z.string(),
  latest_client_project: projectSchema.nullable().optional(),
});

export const userLoginSchema = z.object({
  user: userSchema,
  status: z.string(),
});

export const officialDocumentSchema = z.object({
  id: z.number(),
  client_project_id: z.number(),
  document_type_id: z.number().nullable(),
  type: documentTypesSchema,
  name: z.string(),
  description: z.string(),
  attachments: z.array(z.string()).nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});

export const officialDocumentsSchema = z.array(officialDocumentSchema);

export const settingSchema = z.object({
  id: z.number().nullable(),
  key: z.string().nullable(),
  name: z.string().nullable(),
  value: z.string().nullable(),
  order: z.number().nullable(),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
});

export const contactItemSchema = z.object({
  id: z.number().nullable(),
  key: z.string().nullable(),
  name: z.string().nullable(),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
  settings: z.array(settingSchema).nullable(),
});

export const settingsContactDataSchema = z.array(contactItemSchema);
